
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
<h1>{`Constants`}</h1>
<p>{`You will see that throughout the design system, sometimes you are required to use one of some possible values for a particular prop, for example when choosing a `}<inlineCode parentName="p">{`category`}</inlineCode>{` for a `}<inlineCode parentName="p">{`Button`}</inlineCode>{`. Here are the 3 basic constants we provide for those components (note that some components expose their own constants, like `}<inlineCode parentName="p">{`FlexJustify`}</inlineCode>{` in `}<inlineCode parentName="p">{`Flex`}</inlineCode>{`).`}</p>
<p>{`You can import them like so:`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import {
  Tier,
  Category,
  Size,
  Align,
  Position,
  Color,
  Shade,
  Speed,
  Direction,
  Easing,
} from '@drawbotics/react-drylus';
`}</code></pre>
<table>
<thead parentName="table">
<tr parentName="thead">
<th parentName="tr" {...{
            "align": null
          }}>{`Name`}</th>
<th parentName="tr" {...{
            "align": null
          }}>{`Usage`}</th>
<th parentName="tr" {...{
            "align": null
          }}>{`Values`}</th>
</tr>
</thead>
<tbody parentName="table">
<tr parentName="tbody">
<td parentName="tr" {...{
            "align": null
          }}>{`Size`}</td>
<td parentName="tr" {...{
            "align": null
          }}>{`To define a size, margin or padding`}</td>
<td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`EXTRA_SMALL`}</inlineCode>{`, `}<inlineCode parentName="td">{`SMALL`}</inlineCode>{`, `}<inlineCode parentName="td">{`DEFAULT`}</inlineCode>{`, `}<inlineCode parentName="td">{`LARGE`}</inlineCode>{`, `}<inlineCode parentName="td">{`EXTRA_LARGE`}</inlineCode>{`, `}<inlineCode parentName="td">{`HUGE`}</inlineCode>{`, `}<inlineCode parentName="td">{`EXTRA_HUGE`}</inlineCode>{`, `}<inlineCode parentName="td">{`MASSIVE`}</inlineCode></td>
</tr>
<tr parentName="tbody">
<td parentName="tr" {...{
            "align": null
          }}>{`Category`}</td>
<td parentName="tr" {...{
            "align": null
          }}>{`Used with any component where the color determines a particular kind of action, to differentiate its usage (e.g. buttons or banners)`}</td>
<td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`BRAND`}</inlineCode>{`, `}<inlineCode parentName="td">{`SUCCESS`}</inlineCode>{`, `}<inlineCode parentName="td">{`INFO`}</inlineCode>{`, `}<inlineCode parentName="td">{`WARNING`}</inlineCode>{`, `}<inlineCode parentName="td">{`DANGER`}</inlineCode></td>
</tr>
<tr parentName="tbody">
<td parentName="tr" {...{
            "align": null
          }}>{`Color`}</td>
<td parentName="tr" {...{
            "align": null
          }}>{`Similar to Category except it is tightly linked to the displayed color`}</td>
<td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`RED`}</inlineCode>{`, `}<inlineCode parentName="td">{`BLUE`}</inlineCode>{`, `}<inlineCode parentName="td">{`ORANGE`}</inlineCode>{`, `}<inlineCode parentName="td">{`GREEN`}</inlineCode>{`, `}<inlineCode parentName="td">{`BRAND`}</inlineCode></td>
</tr>
<tr parentName="tbody">
<td parentName="tr" {...{
            "align": null
          }}>{`Tier`}</td>
<td parentName="tr" {...{
            "align": null
          }}>{`Used whenever the hierarchy is needed to determine the usage of a component (e.g. buttons)`}</td>
<td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PRIMARY`}</inlineCode>{`, `}<inlineCode parentName="td">{`SECONDARY`}</inlineCode>{`, `}<inlineCode parentName="td">{`TERTIARY`}</inlineCode></td>
</tr>
<tr parentName="tbody">
<td parentName="tr" {...{
            "align": null
          }}>{`Position`}</td>
<td parentName="tr" {...{
            "align": null
          }}>{`Used to position elements, usually in regards to a parent element`}</td>
<td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`TOP`}</inlineCode>{`, `}<inlineCode parentName="td">{`BOTTOM`}</inlineCode>{`, `}<inlineCode parentName="td">{`LEFT`}</inlineCode>{`, `}<inlineCode parentName="td">{`RIGHT`}</inlineCode></td>
</tr>
<tr parentName="tbody">
<td parentName="tr" {...{
            "align": null
          }}>{`Align`}</td>
<td parentName="tr" {...{
            "align": null
          }}>{`Often used with text components. Layouts have their own align constants`}</td>
<td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`LEFT`}</inlineCode>{`, `}<inlineCode parentName="td">{`CENTER`}</inlineCode>{`, `}<inlineCode parentName="td">{`RIGHT`}</inlineCode></td>
</tr>
<tr parentName="tbody">
<td parentName="tr" {...{
            "align": null
          }}>{`Shade`}</td>
<td parentName="tr" {...{
            "align": null
          }}>{`Used to determine the lightness/darkness of color e.g. in Text`}</td>
<td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`DARK`}</inlineCode>{`, `}<inlineCode parentName="td">{`MEDIUM`}</inlineCode>{`, `}<inlineCode parentName="td">{`LIGHT`}</inlineCode></td>
</tr>
<tr parentName="tbody">
<td parentName="tr" {...{
            "align": null
          }}>{`Speed`}</td>
<td parentName="tr" {...{
            "align": null
          }}>{`Used to determine the speed of animations`}</td>
<td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SLOW`}</inlineCode>{`, `}<inlineCode parentName="td">{`DEFAULT`}</inlineCode>{`, `}<inlineCode parentName="td">{`FAST`}</inlineCode></td>
</tr>
<tr parentName="tbody">
<td parentName="tr" {...{
            "align": null
          }}>{`Direction`}</td>
<td parentName="tr" {...{
            "align": null
          }}>{`Similar to Position, but holds a notion of movement, rather than static position`}</td>
<td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`TOP_DOWN`}</inlineCode>{`, `}<inlineCode parentName="td">{`BOTTOM_UP`}</inlineCode>{`, `}<inlineCode parentName="td">{`LEFT_RIGHT`}</inlineCode>{`, `}<inlineCode parentName="td">{`RIGHT_LEFT`}</inlineCode></td>
</tr>
<tr parentName="tbody">
<td parentName="tr" {...{
            "align": null
          }}>{`Easing`}</td>
<td parentName="tr" {...{
            "align": null
          }}>{`Used to determine the animation curve`}</td>
<td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`LINEAR`}</inlineCode>{`, `}<inlineCode parentName="td">{`EASED`}</inlineCode>{`, `}<inlineCode parentName="td">{`BOUNCY`}</inlineCode></td>
</tr>
</tbody>
</table>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;